import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Users, BookOpen, Sparkles } from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RubricScoringForm from "@/components/RubricScoringForm";
import TeacherResourcesPanel from "@/components/TeacherResourcesPanel";
import SharedScoreBanner from "@/components/SharedScoreBanner";
import TeksCoverageMap from "@/components/TeksCoverageMap";
import FormativeQuiz from "@/components/FormativeQuiz";
import LabNotebookDownload from "@/components/LabNotebookDownload";
import TopicSourcesFooter from "@/components/TopicSourcesFooter";
import TopicApplications from "@/components/TopicApplications";
import Seo from "@/components/Seo";
import {
  PROJECT_TOPICS,
  DELIVERABLES,
  PRESENTATION_RUBRIC,
  type ProjectTopic,
} from "@/data/endOfYearProject";

type Audience = "teacher" | "student";

export default function EndOfYearProject() {
  const [audience, setAudience] = useState<Audience>("teacher");

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="End of Year Chemistry Project — ElementPad" description="Capstone chemistry project framework with rubric and milestone planning for high school classes." path="/teach/end-of-year-project" />
      <AppHeader />
      <Main className="py-8">
        <div className="max-w-[1200px] mx-auto px-4 space-y-10">
          <SharedScoreBanner />
          {/* Hero */}
          <header className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Link to="/lessons" className="hover:text-foreground transition-colors">
                Teach
              </Link>
              <span>/</span>
              <span className="text-foreground">End-of-Year Project</span>
            </div>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="text-xs">Grade 10</Badge>
                  <Badge variant="outline" className="text-xs">TEKS-aligned</Badge>
                  <Badge variant="outline" className="text-xs">Differentiated</Badge>
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">
                  Chemistry End-of-Year Project
                </h1>
                <p className="max-w-2xl text-sm text-muted-foreground">
                  A comprehensive TEKS-based exploration project. Students choose a topic and a
                  pathway, then build a portfolio combining experimentation, visual analysis, and
                  scientific communication.
                </p>
              </div>

              <Tabs value={audience} onValueChange={(v) => setAudience(v as Audience)}>
                <TabsList>
                  <TabsTrigger value="teacher" className="gap-1.5">
                    <GraduationCap size={14} aria-hidden="true" />
                    Teacher view
                  </TabsTrigger>
                  <TabsTrigger value="student" className="gap-1.5">
                    <Users size={14} aria-hidden="true" />
                    Student view
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <Card className="bg-muted/30 border-dashed">
              <CardContent className="py-4 text-sm text-muted-foreground">
                {audience === "teacher" ? (
                  <>
                    <strong className="text-foreground">For teachers:</strong> Assign one or more
                    topics below. Each pathway is mapped to TEKS standards and to ElementPad tools
                    students can use as evidence. The shared rubric and deliverables apply to every
                    pathway.
                  </>
                ) : (
                  <>
                    <strong className="text-foreground">For students:</strong> Pick a topic that
                    interests you, choose a pathway that matches how you learn, and use the linked
                    ElementPad tools to do the work. Every pathway ends with a short class
                    presentation.
                  </>
                )}
              </CardContent>
            </Card>
          </header>

          {/* Topics */}
          <section className="space-y-4">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Topics & pathways</h2>
                <p className="text-sm text-muted-foreground">
                  Ten TEKS-aligned topics (including Lab Foundations). Most include both Foundation and Intermediate pathways.
                </p>
              </div>
              <span className="text-xs text-muted-foreground">{PROJECT_TOPICS.length} topics</span>
            </div>

            {audience === "teacher" && <TeksCoverageMap />}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PROJECT_TOPICS.map((topic) => (
                <TopicCard key={topic.id} topic={topic} audience={audience} />
              ))}
            </div>
          </section>

          {/* Deliverables */}
          <section className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <BookOpen size={18} className="text-primary" aria-hidden="true" />
                Deliverables
              </h2>
              <p className="text-sm text-muted-foreground">
                Required for every pathway, regardless of topic.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {DELIVERABLES.map((d) => (
                <Card key={d.title}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{d.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    {d.description}
                  </CardContent>
                </Card>
              ))}
            </div>
            <LabNotebookDownload />
          </section>

          {/* Presentation rubric */}
          <section className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <Sparkles size={18} className="text-primary" aria-hidden="true" />
                Presentation requirements
              </h2>
              <p className="text-sm text-muted-foreground">
                Every student presents to the class. Foundation expectations shown; raise the bar
                for Intermediate pathways.
              </p>
            </div>
            <Card>
              <CardContent className="p-0">
                <dl className="divide-y divide-border">
                  {PRESENTATION_RUBRIC.map((r) => (
                    <div key={r.label} className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-1 sm:gap-4 px-4 py-3">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-foreground">
                        {r.label}
                      </dt>
                      <dd className="text-sm text-muted-foreground">{r.detail}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </section>

          {audience === "student" && <RubricScoringForm />}
        </div>
      </Main>
    </div>
  );
}

function TopicCard({ topic, audience }: { topic: ProjectTopic; audience: Audience }) {
  const Icon = topic.icon;
  const [tab, setTab] = useState<string>(topic.pathways[0]?.level ?? "Foundation");

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="rounded-md bg-primary/10 text-primary p-2 flex-shrink-0">
              <Icon size={18} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base leading-tight">
                <span className="text-muted-foreground font-mono mr-1.5">
                  {String(topic.number).padStart(2, "0")}
                </span>
                {topic.title}
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                TEKS {topic.teks}
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-4 text-sm">
        <p className="text-sm text-muted-foreground">{topic.objective}</p>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-8">
            {topic.pathways.map((p) => (
              <TabsTrigger key={p.level} value={p.level} className="text-xs h-6 px-2">
                {p.level}
              </TabsTrigger>
            ))}
          </TabsList>
          {topic.pathways.map((p) => (
            <TabsContent key={p.level} value={p.level} className="mt-3 space-y-1.5">
              <p className="text-sm font-medium text-foreground">{p.title}</p>
              <p className="text-sm text-muted-foreground">{p.description}</p>
            </TabsContent>
          ))}
        </Tabs>

        {topic.tools.length > 0 && (
          <div className="mt-auto pt-2 border-t border-border">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-2">
              Use these ElementPad tools
            </p>
            <div className="flex flex-wrap gap-1.5">
              {topic.tools.map((t) => (
                <Link
                  key={t.path}
                  to={t.path}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background hover:bg-muted hover:border-primary/40 px-2 py-1 text-xs text-foreground transition-colors"
                >
                  {t.label}
                  <ArrowRight size={11} aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {audience === "teacher" && <TeacherResourcesPanel topic={topic} />}
        <FormativeQuiz topicId={topic.id} />
        <TopicApplications topicId={topic.id} />
        <TopicSourcesFooter
          topicId={topic.id}
          topicTitle={topic.title}
          topicNumber={topic.number}
        />
      </CardContent>
    </Card>
  );
}
